import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useResendVerification } from '@/hooks/useResendVerification';

/**
 * EmailVerificationBanner
 *
 * Rendered as a full-width bar flush under the navbar (mounted as a sibling
 * of `<main>` in DashboardLayout, sharing the exact same `px-6` gutter — no
 * negative-margin breakout, so its edges always line up with the page
 * content below it, at any viewport width).
 *
 * Shown whenever the logged-in user has emailVerified === false. On-brand
 * teal informational nudge (not a warning) with a one-click resend action.
 *
 * - Renders nothing for verified users or when no user is present.
 * - Dismissible per session (local state only).
 * - "Resend link" briefly disables + shows a confirmation after a successful send.
 */

export const EmailVerificationBanner = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();

  const { status: resendStatus, resend } = useResendVerification();
  const [dismissed, setDismissed] = useState(false);

  // Only render for unverified, logged-in users (and only if not dismissed this session)
  if (!user || user.emailVerified || dismissed) {
    return null;
  }

  const isSending = resendStatus === 'sending';
  const isSent = resendStatus === 'sent';

  // Bold the interpolated email within the translated sentence, without
  // depending on the (unused-elsewhere) <Trans> component: locate the
  // already-interpolated email substring and split around it.
  const message = t('email_unverified_banner_message', { email: user.email });
  const emailIndex = message.indexOf(user.email);
  const messageNode =
    emailIndex === -1 ? (
      message
    ) : (
      <>
        {message.slice(0, emailIndex)}
        <span className="font-semibold text-gray-800 dark:text-gray-200">{user.email}</span>
        {message.slice(emailIndex + user.email.length)}
      </>
    );

  return (
    <div
      className="w-full flex items-center gap-3.5 px-6 py-3 bg-gradient-to-b from-primary-25 to-primary-50 dark:from-primary-500/10 dark:to-primary-900/25 border-b border-primary-100 dark:border-primary-800/40"
      role="status"
      aria-live="polite"
    >
      {/* Icon chip */}
      <span
        className="flex-shrink-0 w-9 h-9 rounded-[10px] bg-primary-100/70 dark:bg-primary-800/40 text-primary-700 dark:text-primary-300 grid place-items-center"
        aria-hidden="true"
      >
        <svg className="w-[19px] h-[19px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <rect x="3" y="5" width="18" height="14" rx="2.5" />
          <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 7 8.5 6 8.5-6" />
        </svg>
      </span>

      {/* Title + message */}
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-100 leading-snug">
          {t('email_unverified_banner_title')}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400 leading-snug mt-0.5">
          {messageNode}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {isSent ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
            <svg className="w-[15px] h-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
            </svg>
            {t('email_unverified_banner_sent')}
          </span>
        ) : (
          <button
            type="button"
            onClick={resend}
            disabled={isSending}
            className="text-xs font-semibold text-primary-600-contrast bg-primary-600 hover:bg-primary-700 disabled:opacity-65 disabled:cursor-default rounded-[9px] px-3.5 py-2 whitespace-nowrap transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 focus-visible:outline-offset-2"
          >
            {isSending ? t('email_unverified_banner_sending') : t('email_unverified_banner_resend')}
          </button>
        )}

        {/* Dismiss (session-only) */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="w-[30px] h-[30px] rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-primary-600/10 grid place-items-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 focus-visible:outline-offset-2"
          aria-label={t('dismiss')}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
